import { ButtonRow } from '../common/buttonRow';
import { CollapsableSet } from 'acscp/components/common/collapsable';
import {
    Booking,
    BookingLeg,
    Itinerary as ItineraryModel
} from 'acscp/store';
import { FormattedMessage } from 'react-intl';
import { PageTitle } from 'acscp/components/common/pageTitle';
import { FlightPlan } from 'acscp/components/legs/flightPlan/flightPlan';
import { SingleLineFlightPlanTrack } from 'acscp/components/legs/flightPlan/singleLineFlightPlanTrack';
import { SplitLineFlightPlanTrack } from 'acscp/components/legs/flightPlan/splitLineFlightPlanTrack';
import React from 'react';
import { Airports } from './airports';
import { AdditionalInformation } from './additionalInfo';
import { Schedule } from './schedule';
import { AircraftDetails } from './aircraftDetails';
import { CrewDetails } from './crewDetails';
import { PassengerDetails } from './passengerDetails';
import { AcsContacts } from './acsContacts';
import style from './itinerary.module.scss';
import { ExpandAll } from 'acscp/components/itinerary/collapsableSections';
import { FlightStartEndDates } from 'acscp/components/legs/flightStartEndDates';
import { Row } from 'acscp/components/common/row';
import { ViewPdfButton } from '../viewPdf/viewPdfButton';
import { ItineraryPdfUrlRequest } from 'acscp/api/requests';

interface LegacyItineraryProps {
    bookingLegs: BookingLeg[];
    itinerary: ItineraryModel;
    bookingDetails: Booking;
}

export const LegacyItinerary: React.SFC<LegacyItineraryProps> = ({bookingLegs, itinerary, bookingDetails}) => {
    return (
        <section className={style.ItineraryPage}>
            <PageTitle
                title={
                    <FormattedMessage
                        id={'web.components.itinerary.charterItinerary'}
                        defaultMessage={'CHARTER ITINERARY'}
                    />}
            >
                <ButtonRow bookingDetails={bookingDetails}>
                    <ViewPdfButton
                        pdfUrlRequest={new ItineraryPdfUrlRequest(itinerary.id)}
                        pdfId={itinerary.id}
                    />
                </ButtonRow>
            </PageTitle>
            <Row>
                <ButtonRow
                    className={style.MobileOnly}
                    bookingDetails={bookingDetails}
                >
                    <ViewPdfButton
                        pdfUrlRequest={new ItineraryPdfUrlRequest(itinerary.id)}
                        pdfId={itinerary.id}
                    />
                </ButtonRow>
                <div className={style.FlightStartEndDates}>
                    <FlightStartEndDates legs={bookingLegs} />
                </div>
                <FlightPlan
                    className={style.FlightPlan}
                    desktopFlightPlan={
                        <SingleLineFlightPlanTrack
                            legs={bookingLegs}
                            withDurations={false}
                            withBackground={true}
                        />}
                    mobileFlightPlan={<SplitLineFlightPlanTrack legs={bookingLegs}/>}
                />
                <CollapsableSet ExpandAll={ExpandAll}>
                    <Schedule
                        legs={bookingLegs}
                        airports={itinerary.airports}
                        displayVisaMessage={itinerary.passengers.length === 0}
                    />
                    <AircraftDetails
                        aircraft={itinerary.aircraft}
                        legs={bookingLegs}
                        passengers={itinerary.passengers}
                        isLongStandingCustomer={itinerary.isLongStandingCustomer}
                    />
                    <CrewDetails
                        captain={itinerary.captain}
                        firstOfficer={itinerary.firstOfficer}
                        flightAttendant={itinerary.flightAttendant}
                    />
                    <Airports airports={itinerary.airports} />
                    <PassengerDetails passengers={itinerary.passengers} />
                    <AdditionalInformation additionalInformation={itinerary.additionalInformation} />
                    <AcsContacts
                        accountManager={itinerary.accountManager}
                        flightManager={itinerary.flightManager}
                    />
                </CollapsableSet>
            </Row>
        </section>
    );
};
